import type { AuthFormErrorKind } from './auth/authFormError';

/**
 * FND-20 — classes utilitaires Tailwind PARTAGÉES pour les éléments de
 * formulaire récurrents (label, champ, lien, message d'erreur) qui reviennent
 * dans quasiment toutes les pages `/login`, `/register`, `/forgot-password`,
 * `/reset-password`, `/schools/new`... des deux apps. Un seul point à faire
 * évoluer pour tout ticket fonctionnel futur (SCH-03, SCH-06, ENR-01+...)
 * plutôt que des classes utilitaires dupliquées et progressivement
 * divergentes dans chaque page — voir CLAUDE.md, section "Tailwind CSS —
 * thème partagé (FND-20)", pour la convention complète.
 *
 * Le THEME (couleurs/typo) vit dans theme.css (tokens `@theme` Tailwind v4) ;
 * ce fichier ne fait qu'assembler des classes utilitaires COURANTES
 * au-dessus de ce thème — pas de nouveau token ici, uniquement de la
 * composition.
 */

export const pageContainerClassName = 'flex min-h-screen items-center justify-center bg-surface-muted px-4 py-10';

export const formFieldClassName = 'flex flex-col gap-1 text-sm font-medium text-text-secondary';

export const formInputClassName =
  'w-full rounded-md border border-border-strong px-3 py-2 text-sm font-normal text-text shadow-sm ' +
  'placeholder:text-gray-400 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-brand-500/40';

export const formHintClassName = 'text-xs font-normal text-text-muted';

export const linkClassName = 'font-medium text-brand-700 underline-offset-2 hover:text-brand-800 hover:underline';

export const secondaryTextClassName = 'text-sm text-text-tertiary';

/**
 * CTA de navigation stylés comme `Button` (variant="primary"/"secondary")
 * mais rendus en `<Link>` — `Button` ne rend qu'un `<button>`, aucune
 * variante `<Link>` n'existe dans ce composant (AccessDenied.tsx, UX-59).
 * Extraites ici pour être réutilisées par plusieurs pages sans dupliquer la
 * chaîne de classes.
 *
 * `ss-button` en tête (audit tactile Impeccable, 2026-09-07) : ces CTA sont
 * des `<a>`, jamais des `<button>`, donc invisibles pour un sélecteur
 * d'élément. Le marqueur les rattache à la règle `@media (pointer: coarse)`
 * du thème partagé (packages/ui/src/theme.css) qui impose 44px de cible
 * tactile. Même rôle de MARQUEUR sémantique que `ss-button--${variant}` sur
 * le composant `Button` (voir Button.tsx, FND-20) : jamais une classe de
 * style, aucune valeur Tailwind ne lui est attachée.
 */
export const primaryLinkButtonClassName =
  'ss-button inline-flex items-center justify-center rounded-md bg-brand-600 px-4 py-2 text-center text-sm font-medium ' +
  'text-white shadow-sm transition-colors hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-brand-500 focus-visible:ring-offset-1';

export const secondaryLinkButtonClassName =
  'ss-button inline-flex items-center justify-center rounded-md border border-brand-600 bg-white px-4 py-2 text-center ' +
  'text-sm font-medium text-brand-700 shadow-sm transition-colors hover:bg-brand-50 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1';

/**
 * SCH-57 — équivalent `<Link>` du variant `ghost` de `Button` (action secondaire
 * DISCRÈTE dans une ligne de tableau : ni fond, ni bordure, ni `shadow-sm`).
 * Composé à l'identique de ce que `Button` produit pour
 * `variant="ghost" size="default"` — `BASE_STYLES` + `SIZE_STYLES.default` +
 * `VARIANT_STYLES.ghost` (voir Button.tsx) — pour qu'un CTA de navigation rendu
 * en `<Link>` soit visuellement INDISTINCT du bouton "Modifier" à côté duquel il
 * s'affiche (ManageClasses.tsx).
 *
 * ⚠️ Les deux classes `disabled:*` de `BASE_STYLES` sont délibérément OMISES :
 * un `<a>`/`<Link>` n'a pas d'attribut `disabled`, la pseudo-classe CSS ne s'y
 * applique jamais. Les inclure aurait suggéré un état désactivé inexistant.
 *
 * `ss-button` et `ss-button--ghost` en tête : même rôle de MARQUEUR sémantique que
 * pour les deux constantes ci-dessus (cible tactile 44px du thème partagé, et
 * parité avec le marqueur de variant posé par `Button`).
 */
export const ghostLinkButtonClassName =
  'ss-button ss-button--ghost inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm ' +
  'font-medium bg-transparent text-text-tertiary transition-colors hover:bg-surface-raised ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1';

/**
 * Erreurs de VALIDATION côté client (mot de passe trop court, NINEA mal
 * formaté...) — distinctes des `AuthFormError` (voir formErrorClassName
 * ci-dessous) : jamais de `kind` associé (toujours le même style rouge,
 * détecté AVANT tout appel réseau), donc pas de marqueur `ss-form-error--*`
 * à préserver ici, aucun test existant ne s'appuie sur une classe pour ce
 * cas.
 */
export const validationErrorClassName = 'rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700';

/**
 * Couleurs par nature d'erreur — même distinction que `AuthFormErrorKind`
 * (voir authFormError.ts) : `generic` (rouge, cas par défaut — identifiants
 * incorrects, NINEA déjà utilisé...), `locked` (ambre, compte
 * TEMPORAIREMENT verrouillé, pas une vraie erreur de saisie), `network`
 * (gris neutre, panne de connectivité/CORS, pas un problème côté
 * utilisateur — voir authFormError.ts pour le détail complet de cette
 * distinction).
 */
const FORM_ERROR_KIND_STYLES: Record<AuthFormErrorKind, string> = {
  generic: 'border-red-200 bg-red-50 text-red-700',
  // UX-64 : `border-amber-600`/`bg-amber-100` (Figma), texte `amber-800` —
  // même justification de contraste que KpiCard.tsx (voir docs/history/UX.md "UX-64").
  locked: 'border-amber-600 bg-amber-100 text-amber-800',
  network: 'border-border-strong bg-surface-muted text-text-secondary',
};

/**
 * `ss-form-error`/`ss-form-error--${kind}` PRÉSERVÉS tels quels (tests
 * existants de Login/Register/ForgotPassword — `toHaveClass('ss-form-error--
 * network')`, etc., voir CLAUDE.md) : ce ne sont pas des classes de style
 * Tailwind, mais des marqueurs sémantiques d'état sur lesquels les tests
 * s'appuient — les classes Tailwind s'AJOUTENT à côté, elles ne les
 * remplacent pas.
 */
export function formErrorClassName(kind: AuthFormErrorKind): string {
  return [
    'ss-form-error',
    `ss-form-error--${kind}`,
    'rounded-md border px-3 py-2 text-sm',
    FORM_ERROR_KIND_STYLES[kind],
  ].join(' ');
}
